import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import QrScanner from '../components/QrScanner';
import { selfServiceCheckin } from '../services/api';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { ScanLine, CheckCircle, XCircle, Loader2, Info, ArrowLeft, QrCode } from 'lucide-react'; // Adicionado QrCode
import Breadcrumb from '../components/Breadcrumb';
import { Skeleton } from '../components/ui/Skeleton';

function SelfCheckinPage() {
    const { isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();

    const [showScanner, setShowScanner] = useState(false);
    const [scannerError, setScannerError] = useState('');
    const [isCheckingIn, setIsCheckingIn] = useState(false);
    const [lastScannedKey, setLastScannedKey] = useState('');
    const [checkinResult, setCheckinResult] = useState(null); // { success: boolean, message: string, details: object }

    useEffect(() => {
        if (!isAuthenticated) {
            toast.warn('Você precisa estar logado para fazer check-in.');
            logout();
            navigate('/login');
        }
    }, [isAuthenticated, logout, navigate]);

    const extractLocalIdFromUrl = (decodedText) => {
        try {
            const url = new URL(decodedText);
            // Verifica se é a URL de Quick Check-in e extrai o localId
            if (url.pathname.includes('/quick-checkin-public')) {
                const localId = url.searchParams.get('localId');
                if (localId) {
                    return localId;
                }
            }
        } catch (e) {
            // Não é uma URL válida, ou não é a URL esperada.
            // Retorna o texto original, assumindo que é o UUID ou a chave antiga.
        }
        return decodedText;
    };

    const handleScanSuccess = async (decodedText) => {
        setShowScanner(false);
        setScannerError('');
        
        // Extrai o localId se for uma URL, caso contrário, usa o texto original
        const checkinKey = extractLocalIdFromUrl(decodedText);
        setLastScannedKey(checkinKey);
        setCheckinResult(null); // Resetar resultado anterior

        if (!checkinKey) {
            toast.error('QR Code vazio ou inválido.');
            return;
        }
        
        // LOG DE DEBBUG
        console.log("Chave de Check-in enviada ao backend:", checkinKey);

        setIsCheckingIn(true);
        try {
            const response = await selfServiceCheckin(checkinKey);
            setCheckinResult({ success: true, message: response.mensagem, details: response });
            toast.success(response.mensagem);
        } catch (err) {
            console.error('Erro no self-check-in:', err);
            setCheckinResult({ success: false, message: err.message || 'Ocorreu um erro ao fazer check-in.' });
            toast.error(err.message || 'Erro ao fazer check-in.');
        } finally {
            setIsCheckingIn(false);
        }
    };

    const handleScanError = (errorMessage) => {
        const errorString = String(errorMessage);
        if (errorString.includes("NotAllowedError") || errorString.includes("NotFoundError")) {
            setScannerError('Permissão de câmera negada ou câmera não encontrada.');
            toast.error('Erro no scanner: Permissão de câmera negada ou câmera não encontrada.');
            setShowScanner(false);
        } else {
            setScannerError(`Erro no scanner: ${errorString}`);
            // toast.error(`Erro no scanner: ${errorString}`); // Pode ser muito verboso
        }
    };

    const breadcrumbItems = [
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Meu Check-in' }
    ];

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 mb-6">
                <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full sm:w-auto">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Voltar ao Dashboard
                </Button>
                <h1 className="text-3xl font-bold tracking-tight text-center sm:text-left w-full sm:w-auto">Self-Service Check-in</h1>
            </div>

            <Card className="w-full max-w-2xl mx-auto">
                <CardHeader className="text-center">
                    <CardTitle className="flex items-center justify-center mb-2"><ScanLine className="mr-2 h-5 w-5" /> Faça seu Check-in</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Aponte a câmera para o **QR Code da Sala/Local da Sessão** para registrar sua presença.
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center space-y-4">
                    
                    {/* Instrução Visual Reforçada */}
                    <div className="w-full p-4 border border-primary/50 rounded-md bg-primary/10 text-sm text-primary flex items-center justify-center gap-3">
                        <QrCode className="h-6 w-6 flex-shrink-0" />
                        <p className="font-medium">
                            ATENÇÃO: Você deve escanear o QR Code **exibido na sala**, e não o seu QR Code pessoal.
                        </p>
                    </div>

                    {!showScanner && (
                        <Button onClick={() => setShowScanner(true)} disabled={isCheckingIn} className="w-full max-w-xs">
                            <ScanLine className="mr-2 h-5 w-5" /> Iniciar Scanner
                        </Button>
                    )}

                    {showScanner && (
                        <div className="w-full p-4 border rounded-md bg-muted/40">
                            <QrScanner onScanSuccess={handleScanSuccess} onScanError={handleScanError} />
                            {scannerError && <p className="text-destructive text-sm mt-2 text-center">{scannerError}</p>}
                            <Button variant="secondary" onClick={() => setShowScanner(false)} className="mt-4 w-full">
                                <XCircle className="mr-2 h-4 w-4" /> Fechar Scanner
                            </Button>
                        </div>
                    )}

                    {isCheckingIn && (
                        <div className="flex items-center justify-center p-4 text-primary">
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            <p>Processando check-in...</p>
                        </div>
                    )}

                    {checkinResult && (
                        <Card className={`w-full ${checkinResult.success ? 'border-green-500' : 'border-red-500'} transition-all duration-200 ease-in-out hover:shadow-md`}>
                            <CardHeader>
                                <CardTitle className={`flex items-center mb-2 ${checkinResult.success ? 'text-green-600' : 'text-destructive'}`}>
                                    {checkinResult.success ? <CheckCircle className="mr-2 h-5 w-5" /> : <XCircle className="mr-2 h-5 w-5" />}
                                    {checkinResult.success ? 'Check-in Confirmado!' : 'Erro no Check-in'}
                                </CardTitle>
                                <CardDescription className="leading-relaxed">{checkinResult.message}</CardDescription>
                            </CardHeader>
                            {checkinResult.details && (
                                <CardContent className="space-y-2 text-sm">
                                    <p><strong>Participante:</strong> {checkinResult.details.nomeParticipante}</p>
                                    <p><strong>Sessão:</strong> {checkinResult.details.nomeSessao}</p>
                                    <p><strong>Local:</strong> {checkinResult.details.nomeLocalSessao}</p>
                                    <p><strong>Horário:</strong> {new Date(checkinResult.details.horarioCheckin).toLocaleString('pt-BR')}</p>
                                </CardContent>
                            )}
                        </Card>
                    )}

                    {lastScannedKey && !isCheckingIn && !checkinResult && (
                        <div className="flex items-center justify-center p-4 text-muted-foreground">
                            <Info className="mr-2 h-5 w-5" />
                            <p>Último QR Code escaneado: <span className="font-mono text-sm">{lastScannedKey}</span></p>
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-center border-t pt-4">
                    <p className="text-sm text-muted-foreground">
                        O registro de presença é essencial para a emissão do seu certificado.
                    </p>
                </CardFooter>
            </Card>
        </div>
    );
}

export default SelfCheckinPage;